import React from 'react';
import { observer } from 'mobx-react-lite';
import {
  colors,
  StapleTable,
  StapleTableBody,
  StapleTableHeader,
  StapleTableRow,
} from '@insurancemenu/imenu-components-lib';
import { ModalType } from '@insurancemenu/imenu-components-lib/dist/helpers/ModalsHelper';
import { ProductLineId } from '@insurancemenu/imenu-components-lib/dist/types/ProductLineId';
import RequiredCrossSellModalDelegate from 'store/ui-stores/browsing/requiredCrossSellModalDelegate';
import { ChevronRight, ErrorOutline } from '@insurancemenu/imenu-components-lib/dist/icons';
import { PRODUCT_LINES_CONTENT_MAP } from '@insurancemenu/imenu-components-lib/dist/utils/ProductLinesHelper';
import {
  ActionModalStyled,
  AddedProductLineNameStyled,
  ButtonStyled,
  HintStyled,
  InfoButtonFilledStyled,
  ModalContentStyled,
  RequiredProductLineNameStyled,
  StapleTableCellStyled,
  StapleTableHeadingStyled,
} from './styles';

interface Props {
  modalStore: RequiredCrossSellModalDelegate;
  changeProductLine: (productLineId: ProductLineId) => void;
}

const RequiredCrossSellModal = observer(({ modalStore, changeProductLine }: Props): JSX.Element => {
  const { isVisible, closeModal, violations, isLoading } = modalStore;

  const onAddRequiredProduct = (productLineId: ProductLineId): void => {
    closeModal();
    changeProductLine(productLineId);
  };

  return (
    <ActionModalStyled
      show={isVisible}
      title="Required Plans Missing"
      subtitle={
        <span>
          The plans you added must be paired with specific plans,
          <br /> please add these to your proposal
        </span>
      }
      modalType={ModalType.NO_ICON}
      icon={<ErrorOutline width="5.6rem" height="5.6rem" color={colors.red[600]} />}
      onClose={closeModal}
      modalContent={
        <ModalContentStyled>
          <HintStyled>
            <InfoButtonFilledStyled width="2.4rem" height="2.4rem" color={colors.red[600]} />
            <span>To save your proposal, please complete the pairings below by adding the required plans</span>
          </HintStyled>

          <StapleTable
            totalElements={violations.length}
            minHeight="25rem"
            maxHeight="none"
            showRowHover={false}
            loading={isLoading}
          >
            <StapleTableHeader>
              <StapleTableRow>
                <StapleTableHeadingStyled width="10rem">Chosen Plan</StapleTableHeadingStyled>
                <StapleTableHeadingStyled width="10rem">Required Plan</StapleTableHeadingStyled>
                <StapleTableHeadingStyled width="8rem" />
              </StapleTableRow>
            </StapleTableHeader>
            <StapleTableBody>
              {violations.map((violation) => (
                <StapleTableRow key={violation.requiredPortfolioId}>
                  <StapleTableCellStyled width="10rem">
                    {violation.addedPortfolioName}
                    <br />
                    <AddedProductLineNameStyled>
                      ({PRODUCT_LINES_CONTENT_MAP[violation.addedProductLineId].label})
                    </AddedProductLineNameStyled>
                  </StapleTableCellStyled>
                  <StapleTableCellStyled width="10rem">
                    {violation?.requiredPortfolioName && (
                      <>
                        {violation.requiredPortfolioName}
                        <br />
                      </>
                    )}
                    <RequiredProductLineNameStyled>
                      ({PRODUCT_LINES_CONTENT_MAP[violation.requiredProductLineId].label})
                    </RequiredProductLineNameStyled>
                  </StapleTableCellStyled>
                  <StapleTableCellStyled width="8rem">
                    <ButtonStyled
                      color="primary"
                      rightIconProp={<ChevronRight />}
                      size="medium"
                      uppercase
                      onClick={(): void => onAddRequiredProduct(violation.requiredProductLineId)}
                    >
                      View Plans
                    </ButtonStyled>
                  </StapleTableCellStyled>
                </StapleTableRow>
              ))}
            </StapleTableBody>
          </StapleTable>
        </ModalContentStyled>
      }
    />
  );
});

export default RequiredCrossSellModal;
